import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import { show as showAuthPopup } from 'client/shared/reducers/popup-reducer'

import prepareComponent from 'client/shared/decorators/prepare-component'
import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'
import urlFor from 'shared/tools/url-helper'

import * as codeActions from 'client/bookmate/reducers/code-reducer'

import Meta from 'client/shared/blocks/meta'
import MetaTitle from 'client/shared/blocks/meta-title'
import { MetaDescription } from 'client/shared/blocks/meta-description'
import Footer from 'client/shared/blocks/footer'
import Spacer from 'client/shared/blocks/spacer'
import { CodeLayout } from 'client/bookmate/blocks/code-index-layout'

import { Dispatch, State as ConnectedState } from 'shared/types/redux'
import { Location } from 'client/shared/types/react-router'
import { CurrentUserState } from 'client/shared/types/current-user'
import { State as CodeState } from 'client/bookmate/reducers/code-reducer'
import { RetentionOfferData } from '../reducers/unsubscribe-reducer'
import { AudiobookProps } from 'client/shared/types/audiobook'
import { BookProps } from 'client/shared/types/book'

type Props = {
  location: Location
  code: CodeState
  dispatch: Dispatch
  currentUser: CurrentUserState
  retentionOfferData: RetentionOfferData | null
  isDesktopAllSizes(): boolean
  attributes: {
    params: {
      content_type: string
      slug: string
    }
  }[]
} & withI18nProps &
  DecoratorProps

type State = {
  isButtonActive: boolean
}

class CodePage extends Component<Props, State> {
  state = {
    isButtonActive: Boolean(this.props.code.codeValue),
  }
  componentDidMount() {
    const {
      dispatch,
      location: { query },
    } = this.props

    window.scrollTo(0, 0)

    if (query?.gift) {
      dispatch(push(urlFor.giftСard(query?.gift, query)))
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const {
      auth,
      dispatch,
      code: { codeValue },
      location,
    } = this.props

    if (auth && !prevProps.auth && codeValue) {
      dispatch(codeActions.checkCode({ codeValue, query: location.search }))
    }
  }

  handleInput = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const { dispatch } = this.props
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const value = e.target?.value

    if (!this.state.isButtonActive && value?.length)
      this.setState({ isButtonActive: true })
    else if (this.state.isButtonActive && !value?.length)
      this.setState({ isButtonActive: false })

    dispatch(
      codeActions.setCode({
        codeValue: value,
        autocomplete: false,
        error: '',
      }),
    )
  }

  handleForm = (e: React.SyntheticEvent<HTMLFormElement>): void => {
    e.preventDefault()
    const {
      currentUser: { auth },
      code: { codeValue },
      dispatch,
      location,
    } = this.props
    if (!codeValue) return

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const isError = e.target.children[0]?.classList.value?.includes('error')
    if (isError)
      return dispatch(
        codeActions.setCode({
          codeValue: '',
          autocomplete: false,
          error: '',
        }),
      )

    if (auth) {
      dispatch(codeActions.checkCode({ codeValue, query: location.search }))
    } else dispatch(showAuthPopup())
  }

  render(): JSX.Element {
    const {
      code,
      i18n,
      dispatch,
      attributes,
      isMobileSize,
      retentionOfferData,
      currentUser: {
        data: { locale, country },
      },
    } = this.props

    const title = i18n.t`code.head_title`
    const description = i18n.t`code.meta_description`
    const ogTitle = i18n.t`code.og_title`
    const ogDescription = i18n.t`code.og_description`

    return (
      <Fragment>
        <MetaTitle title={title} og={ogTitle} />
        <MetaDescription description={description} og={ogDescription} />
        <Meta
          // eslint-disable-next-line prettier/prettier, @typescript-eslint/ban-ts-comment
          // @ts-ignore
          content={`${ASSETS_PATH}sharing/code.png?v3`}
          property="twitter:image"
        />
        <Meta
          // eslint-disable-next-line prettier/prettier, @typescript-eslint/ban-ts-comment
          // @ts-ignore
          content={`${ASSETS_PATH}sharing/code.png?v3`}
          property="og:image"
        />
        <CodeLayout
          isMobile={isMobileSize()}
          resources={{
            books: retentionOfferData?.appended as BookProps[],
            audiobooks: retentionOfferData?.bestsellers as AudiobookProps[],
          }}
          country={country}
          dispatch={dispatch}
          locale={locale}
          disabled={!this.state.isButtonActive}
          code={code}
          handleForm={this.handleForm}
          handleInput={this.handleInput}
          booksParams={attributes[0]?.params}
          audiobooksParams={attributes[1]?.params}
        />
        <Spacer size={64} />
        <Footer />
      </Fragment>
    )
  }
}

const prepareWrapper = prepareComponent(async function ({ store, location }) {
  const { dispatch } = store
  const { query } = location
  const {
    promo: codeValue,
    redirect_entity: entity,
    redirect_entity_id: id,
  } = query
  const redirectUrl =
    entity && id ? `/${[entity, id].map(encodeURIComponent).join('/')}` : null

  if (codeValue) {
    dispatch(
      codeActions.setCode({
        codeValue,
        redirectUrl,
      }),
    )
  }
})

const connectWrapper = connect((state: ConnectedState) => ({
  currentUser: state.currentUser,
  code: state.code,
  attributes: state.showcase.rootNavigation.items,
  retentionOfferData: state.unsubscribeProcess?.retentionOfferData,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  withI18n({ update: true }),
  uiBox,
)

export default wrappers(CodePage)
